import React, { useState, useEffect } from "react";
import { getLocale } from "egov-ui-kit/utils/localStorageUtils";
import { formatINR, sumField, safeArray } from "egov-ui-kit/utils/commons";

const LEVELS = [
  { key: "ulbs", label: "ULB Name", childKey: "zones", hasTaxSplit: true },
  { key: "zones", label: "Zone Name", childKey: "departments", hasTaxSplit: false },
  { key: "departments", label: "Department", childKey: null, hasTaxSplit: false },
];

const normalizeDepartmentList = (apiResponse) =>
  safeArray(apiResponse).map((dept, idx) => ({
    id: dept.departmentName ? `${dept.departmentName}_${idx}` : `dept_${idx}`,
    name: dept.departmentName,
    totalReceipts: dept.departmentTotalAmount,
    taxHeadDetails: safeArray(dept.taxHeadDetails), // isi se account-wise detail banegi, no extra API
  }));

// Tax Receipts click → "TR", Non-Tax Receipts click → "NTR", Total Receipts click → "NA"
const getReceiptTypeCode = (type) => {
  if (type === "taxReceipts") return "TR";
  if (type === "nonTaxReceipts") return "NTR";
  return "NA";
};

// Department item ke taxHeadDetails ko account-wise receipts me convert karo (glCode se group/sum)
const buildReceiptsFromTaxHeadDetails = (taxHeadDetails) => {
  const map = new Map();
  safeArray(taxHeadDetails).forEach((r) => {
    const key = r.glCode;
    if (!map.has(key)) {
      map.set(key, {
        id: key,
        accountHeadName: r.taxHead,
        accountCode: r.glCode,
        totalReceipt: 0,
      });
    }
    map.get(key).totalReceipt += Number(r.amount) || 0;
  });
  return Array.from(map.values());
};

// ─────────────────────────────────────────────
// RECEIPT DETAIL COMPONENT  (Account-wise, opens on Department click)
// ─────────────────────────────────────────────
const ReceiptDetailTable = ({ item, financialYear, ulbName, onBack }) => {
  const receipts = safeArray(item.receipts);
  const totalAmount = sumField(receipts, "totalReceipt");

  return (
    <div className="receipt-card">
      <div className="zone-wise-header">
        <div className="header wrapper-header">
          <button className="btn btn-secondary back-button" onClick={onBack}>
            Back
          </button>
          <div>
            <h2>{item.name}</h2>
          </div>
        </div>
        <div className="receipt-header">
          <span>Financial Year : {financialYear}</span>
          <span>|</span>
          <span>ULB : {ulbName}</span>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table receipt-table mb-0">
          <thead>
            <tr>
              <th>Account Name</th>
              <th>Account Code</th>
              <th style={{ textAlign: "right" }}>Total Receipt</th>
            </tr>
          </thead>
          <tbody>
            {receipts.length > 0 ? (
              <React.Fragment>
                {receipts.map((r) => (
                  <tr key={r.id}>
                    <td>{r.accountHeadName}</td>
                    <td>{r.accountCode}</td>
                    <td style={{ textAlign: "right" }}>{formatINR(r.totalReceipt)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td colSpan="2">Total</td>
                  <td style={{ textAlign: "right" }}>{formatINR(totalAmount)}</td>
                </tr>
              </React.Fragment>
            ) : (
              <tr>
                <td colSpan="3" style={{ textAlign: "center" }}>
                  No Receipts Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TaxBreakdownTable = ({ breakdown, financialYear, ulbName, onBack, onDrillDown }) => {
  const rows = safeArray(breakdown.rows);
  const total = sumField(rows, breakdown.type);

  return (
    <div className="receipt-card">
      <div className="zone-wise-header">
        <div className="header wrapper-header">
          <button className="btn btn-secondary back-button" onClick={onBack}>
            Back
          </button>
          <div>
            <h2>{breakdown.title}</h2>
          </div>
        </div>
        <div className="receipt-header">
          <span>Financial Year : {financialYear}</span>
          <span>|</span>
          <span>ULB : {ulbName}</span>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table receipt-table mb-0">
          <thead>
            <tr>
              <th>Zone Name</th>
              <th>Total Receipts (₹)</th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              <React.Fragment>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.name}</td>
                    <td>
                      {r[breakdown.type] ? (
                        <a href="#"
                          className="link-cell"
                          onClick={function (e) {
                            e.preventDefault();
                            onDrillDown(r);
                          }}
                        >
                          {formatINR(r[breakdown.type])}
                        </a>
                      ) : (
                        formatINR(r[breakdown.type])
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td>Total</td>
                  <td>{formatINR(total)}</td>
                </tr>
              </React.Fragment>
            ) : (
              <tr>
                <td colSpan="2" style={{ textAlign: "center" }}>
                  No Record Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const TotalReceipts = ({
  receiptsOrderDetails,
  fetchDepartmentReceiptDetails, // Zone click par yahi API call hogi
  departmentReceiptDetails, // Uska response (array of departments) yahin aayega
  ulbType,
  fromDate,
  toDate,
}) => {
  const tableData = receiptsOrderDetails && receiptsOrderDetails;

  const [breadcrumb, setBreadcrumb] = useState([]);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [taxBreakdown, setTaxBreakdown] = useState(null); // { type, title, rows } — ULB level only
  const [pendingZone, setPendingZone] = useState(null); // zone jiska API abhi call hui hai
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  // ── Jab zone click hoke API response (departmentReceiptDetails) aaye ──
  useEffect(() => {
    if (pendingZone && departmentReceiptDetails) {
      const departments = normalizeDepartmentList(departmentReceiptDetails);
      setBreadcrumb(function (prev) {
        return prev.concat([{ levelIndex: prev.length, item: { ...pendingZone, departments } }]);
      });
      setLoadingDepartments(false);
      setPendingZone(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentReceiptDetails]);

  // Zone row click INSIDE the Tax/Non-Tax breakdown table → same department API call
  // as the normal Zone-level Total column click. Pushes the ULB item into breadcrumb
  // first (so levelIndex becomes 1 / zones), then reuses the existing pendingZone flow —
  // once departmentReceiptDetails arrives, the useEffect pushes the departments in and
  // the normal Department table (2-column) is shown automatically.
  const handleTaxBreakdownDrillDown = (zoneItem) => {
    var ulbItem = taxBreakdown.parentItem;
    var receiptType = getReceiptTypeCode(taxBreakdown.type); // "TR" or "NTR"

    setBreadcrumb([{ levelIndex: 0, item: ulbItem }]);
    setPendingZone(zoneItem);
    setLoadingDepartments(true);
    setTaxBreakdown(null);

    const payload = {
      financialYear: tableData.financialYear,
      ulbName: ulbItem ? ulbItem.tenantId : null,
      zoneName: zoneItem.name,
      ulbType: ulbType,
      fromDate: fromDate,
      toDate: toDate,
      locale: getLocale(),
      receiptDetail: receiptType,
    };

    fetchDepartmentReceiptDetails(payload);
  };

  // ── Tax / Non-Tax breakdown view (opened from ULB row's Tax/Non-Tax click, no API) ──
  if (taxBreakdown) {
    return (
      <TaxBreakdownTable
        breakdown={taxBreakdown}
        financialYear={tableData.financialYear}
        ulbName={tableData.ulbName}
        onBack={() => setTaxBreakdown(null)}
        onDrillDown={handleTaxBreakdownDrillDown}
      />
    );
  }

  // ── Receipt detail view (account-wise, opened from Department — no API call) ──
  if (selectedReceipt) {
    return (
      <ReceiptDetailTable
        item={selectedReceipt}
        financialYear={tableData.financialYear}
        ulbName={tableData.ulbName}
        onBack={() => setSelectedReceipt(null)}
      />
    );
  }

  // ── Derived ────────────────────────────────
  const levelIndex = breadcrumb.length;
  const levelConfig = LEVELS[levelIndex];
  const isLastLevel = levelConfig.childKey === null; // Department level
  const isRootLevel = levelIndex === 0;
  const isZoneLevel = levelConfig.key === "zones"; // is level ka item click -> API call

  const currentRows = (function () {
    if (isRootLevel) {
      return safeArray(tableData[LEVELS[0].key]);
    }
    var parentItem = breadcrumb[levelIndex - 1].item;
    var currentKey = LEVELS[levelIndex].key;
    return safeArray(parentItem[currentKey]);
  })();

  const currentTitle = isRootLevel ? "ULB Wise Receipts" : breadcrumb[levelIndex - 1].item.name;

  const totalsRow = {
    totalTaxReceipts: sumField(currentRows, "taxReceipts"),
    totalNonTaxReceipts: sumField(currentRows, "nonTaxReceipts"),
    totalReceipts: sumField(currentRows, "totalReceipts"),
  };

  // ── Handlers ───────────────────────────────
  // type: undefined/"total" → normal Total Receipts column click
  //       "taxReceipts" / "nonTaxReceipts" → those columns' click (ULB level only)
  const handleDrillDown = (item, type) => {
    if (type === "taxReceipts" || type === "nonTaxReceipts") {
      // Zone-wise breakdown using already-loaded zones data, no API call needed.
      const childRows = safeArray(item[levelConfig.childKey]);
      setTaxBreakdown({
        type: type,
        title: item.name,
        rows: childRows,
        parentItem: item, // ULB item, needed if a zone row here is drilled into departments
      });
      return;
    }

    if (isLastLevel) {
      // Department click → sirf local taxHeadDetails se account-wise table (no API)
      const receipts = buildReceiptsFromTaxHeadDetails(item.taxHeadDetails);
      setSelectedReceipt({ ...item, receipts });
      return;
    }

    if (isZoneLevel) {
      // Zone click → API call, response aane tak breadcrumb push nahi hoga
      setPendingZone(item);
      setLoadingDepartments(true);

      const payload = {
        financialYear: tableData.financialYear,
        ulbName: breadcrumb[0] ? breadcrumb[0].item.tenantId : null,
        zoneName: item.name,
        ulbType: ulbType,
        fromDate: fromDate,
        toDate: toDate,
        locale: getLocale(),
        receiptType: getReceiptTypeCode(),
      };

      fetchDepartmentReceiptDetails(payload);
      return;
    }

    // ULB level click → normal breadcrumb push
    setBreadcrumb(function (prev) {
      return prev.concat([{ levelIndex: levelIndex, item: item }]);
    });
  };

  const handleBack = () => {
    setBreadcrumb(function (prev) {
      return prev.slice(0, -1);
    });
  };

  const handleBreadcrumbClick = (toIndex) => {
    setBreadcrumb(function (prev) {
      return prev.slice(0, toIndex);
    });
  };

  // ── Render ─────────────────────────────────
  return (
    <div className="receipt-card">
      <div className="zone-wise-header">
        <div className="header wrapper-header">
          {isRootLevel ? (
            <div className="icon-wrapper">
              <i className="material-icons">description</i>
            </div>
          ) : (
            <button className="btn btn-secondary back-button" onClick={handleBack}>
              Back
            </button>
          )}
          <div>
            <h2>{currentTitle}</h2>
          </div>
        </div>
        <div className="receipt-header">
          <span>Financial Year : {tableData.financialYear}</span>
          <span>|</span>
          <span>ULB : {tableData.ulbName}</span>
        </div>
      </div>

      {!isRootLevel && (
        <div className="breadcrumb-bar">
          <span className="bc-link" onClick={() => handleBreadcrumbClick(0)} role="button" tabIndex={0}>
            ULB Wise Receipts
          </span>
          {breadcrumb.map(function (bc, idx) {
            var isLast = idx === breadcrumb.length - 1;
            return (
              <React.Fragment key={bc.item.id || idx}>
                <span className="bc-sep"> › </span>
                {isLast ? (
                  <span className="bc-current">{bc.item.name}</span>
                ) : (
                  <span className="bc-link" onClick={() => handleBreadcrumbClick(idx + 1)} role="button" tabIndex={0}>
                    {bc.item.name}
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      <div className={`table-responsive ${levelConfig.key === "ulbs" || levelConfig.key === "zones" ? "scrolling-table" : ""}`}>
        {levelConfig.hasTaxSplit ? (
          <table className="table receipt-table mb-0">
            <thead>
              <tr>
                <th>{levelConfig.label}</th>
                <th>Tax Receipts (₹)</th>
                <th>Non-Tax Receipts (₹)</th>
                <th>Total Receipts (₹)</th>
              </tr>
            </thead>
            <tbody>
              {currentRows.length > 0 ? (
                <React.Fragment>
                  {currentRows.map(function (item) {
                    return (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>
                          {item.taxReceipts ? (

                            <a href="#"
                              className="link-cell"
                              onClick={function (e) {
                                e.preventDefault();
                                handleDrillDown(item, "taxReceipts");
                              }}
                            >
                              {isZoneLevel && loadingDepartments && pendingZone && pendingZone.id === item.id
                                ? "Loading..."
                                : formatINR(item.taxReceipts)}
                            </a>
                          ) : (
                            formatINR(item.taxReceipts)
                          )}
                        </td>
                        <td>
                          {item.nonTaxReceipts ? (

                            <a href="#"
                              className="link-cell"
                              onClick={function (e) {
                                e.preventDefault();
                                handleDrillDown(item, "nonTaxReceipts");
                              }}
                            >
                              {isZoneLevel && loadingDepartments && pendingZone && pendingZone.id === item.id
                                ? "Loading..."
                                : formatINR(item.nonTaxReceipts)}
                            </a>
                          ) : (
                            formatINR(item.nonTaxReceipts)
                          )}
                        </td>
                        <td>
                          {item.totalReceipts ? (

                            <a href="#"
                              className="link-cell"
                              onClick={function (e) {
                                e.preventDefault();
                                handleDrillDown(item);
                              }}
                            >
                              {isZoneLevel && loadingDepartments && pendingZone && pendingZone.id === item.id
                                ? "Loading..."
                                : formatINR(item.totalReceipts)}
                            </a>
                          ) : (
                            formatINR(item.totalReceipts)
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ) : (
                <tr>
                  <td colSpan="4" style={{ textAlign: "center" }}>
                    No Record Found
                  </td>
                </tr>
              )}
            </tbody>
            {currentRows.length > 0 && (
              <tfoot className="tfoot-bottom">
                <tr className="total-row">
                  <td style={{ textAlign: "left" }}>Total</td>
                  <td>{formatINR(totalsRow.totalTaxReceipts)}</td>
                  <td>{formatINR(totalsRow.totalNonTaxReceipts)}</td>
                  <td>{formatINR(totalsRow.totalReceipts)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        ) : (
          <table className="table receipt-table mb-0">
            <thead>
              <tr>
                <th>{levelConfig.label}</th>
                <th>Total Receipts (₹)</th>
              </tr>
            </thead>
            <tbody>
              {currentRows.length > 0 ? (
                <React.Fragment>
                  {currentRows.map(function (item) {
                    return (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>
                          <a
                            href="#"
                            className="link-cell"
                            onClick={function (e) {
                              e.preventDefault();
                              handleDrillDown(item);
                            }}
                          >
                            {isZoneLevel && loadingDepartments && pendingZone && pendingZone.id === item.id
                              ? "Loading..."
                              : formatINR(item.totalReceipts)}
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="total-row">
                    <td>Total</td>
                    <td>{formatINR(totalsRow.totalReceipts)}</td>
                  </tr>
                </React.Fragment>
              ) : (
                <tr>
                  <td colSpan="2" style={{ textAlign: "center" }}>
                    No Record Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TotalReceipts;


// WEBPACK FOOTER //
// src/modules/employee/Home/TotalReceipts.js